import {Dispatch, SetStateAction, useEffect, useState} from "react";
import { IFormHandler } from "../forms/forms";

export interface IForm<V> {
    register(prop: keyof V): {
        value: V;
        onChange(value: keyof V): void;
        errors: any; 
    };
    reset(): void;
    watch(prop: keyof V): any;
    getValues(): any;
    setErrors: Dispatch<SetStateAction<any>>;
    errors: any;
    setLoading: Dispatch<SetStateAction<boolean>>;
    isLoading(): boolean;
    change(prop?: keyof V): (value: any | V) => void;
}

export interface IFormOptions<V> {
    name?: string;
    listener?(values: V): void;
    prevFormErrors?: any;
}

export const useForm = <V extends object, M extends object = {}>(defaultValue: V | any = {} as V | IFormHandler<V, M>, options: IFormOptions<V> = {}) => {
    let hasHandler = false;
    let initialValues: V;
    if (
        defaultValue &&
        typeof defaultValue === "object" &&
        typeof (defaultValue as IFormHandler<V, M>).toForm === "function"
    ) {
        hasHandler = true;

        initialValues = (defaultValue as IFormHandler<V, M>).initialValue;
    } else {
        initialValues = defaultValue as V;
    }

    const [loading, setLoading] = useState<boolean>(false);
    const [values, setValues] = useState<V>(initialValues);
    const [errors, setErrors] = useState<any>();

    const handleChange = (prop?: keyof V) => (value: any) => {
        setValues((prevState: V) => {
            if (!!prop) {
                return {
                    ...prevState,
                    [prop]: value,
                };
            }

            if (!prop && typeof value === 'object') {
                return {
                    ...prevState,
                    ...value,
                };
            }

            return prevState;
        });
    };

    const register = (prop: keyof V) => {
        return {
            label: generateLabelForProp(prop as string),
            value: values[prop] as any || '',
            onChange: handleChange(prop),
            errors: !!options.name ? errors?.getFromMap(`${options.name}_${prop as any}`) : errors?.getFromMap(prop),
        };
    };

    useEffect(() => {
        if (!options.listener) {
            return;
        }
        options.listener(values);
    }, [values]);

    useEffect(() => {
        if (options.prevFormErrors === undefined) {
            return;
        }

        setErrors(options.prevFormErrors);
    }, [options.prevFormErrors]);

    const reset = () => {
        const data = {} as any;
        Object.keys(values).forEach(key => {
            data[key] = '';
        });

        handleChange()({...data, ...initialValues});
    };

    const watch = (prop: keyof V): any => {
        return values[prop];
    };

    const isLoading = (): boolean => {
        return loading;
    }

    const getValues = (): V => {
        return values;
    }

    return {
        register,
        reset,
        change: handleChange,
        watch, watchAll: getValues, getValues,
        setErrors, errors,
        setLoading, isLoading,

        ...(!hasHandler ? {
            toRequest: (_?: V): M => { throw new Error('Not supported. Use a FormHandler instead.'); },
        } : {
            toRequest: (defaultValue as IFormHandler<V, M>).toRequest,
        }),
    };
};

const camelToSnakeCase = (text: string): string => {
    return text.replace(/([A-Z])/g, '_$1').toLowerCase();
}
  
const generateLabelForProp = (prop: string): string => {
    const label = camelToSnakeCase(prop).replace(/_id$/, '').replaceAll('_', ' ');
    return label[0].toUpperCase() + label.slice(1);
};
